import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

import { ItemPerPageSelector } from '@components/common'
import { AdasBox, AdasPagination } from '@components/wrapper-components'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { productCategories } from '@components/product'
import { currencyFormatter } from '../../../utils/utils'

const renderCategory = (content) => {
  if (!content?.category) return '-'
  return productCategories[content.category] || content.category?.name || '-'
}

export const ProductList = ({ products = [], onSelectProduct, columns, pageParams, pageSizeHandler, paginationHandler }) => {
  const [productsDisplayed, setProductsDisplayed] = useState(products)
  const [sort, setSort] = useState('ASC')
  const initialLoad = useRef(true)

  const clickSortHandler = (id) => {
    if (id !== 'name') return
    initialLoad.current = false
    setSort((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))
  }

  useEffect(() => {
    if (!initialLoad.current) {
      setProductsDisplayed((prevList) => [...prevList].reverse())
    }
  }, [sort])

  useEffect(() => {
    setProductsDisplayed(products)
  }, [products])

  const renderCell = (content, column) => {
    switch (column.id) {
      case 'name':
        return (
          <button
            type='button'
            onClick={(e) => {
              e.preventDefault()
              onSelectProduct(content, 'name')
            }}
            className='text-[#1F6FEB] text-[13px] font-medium hover:underline text-left'
          >
            {content.name}
          </button>
        )
      case 'category':
        return <span className='text-[13px] text-[#0A1628]'>{renderCategory(content)}</span>
      case 'price':
        return <span className='text-[13px] text-[#0A1628]'>{currencyFormatter(content.price)}</span>
      case 'is_active':
        return content.is_active ? (
          <span className='inline-flex items-center justify-center rounded-full bg-[#DCFCE7] px-3 py-1 text-[12px] font-semibold text-[#16803F]'>
            Active
          </span>
        ) : (
          <span className='inline-flex items-center justify-center rounded-full bg-[#EBEDF0] px-3 py-1 text-[12px] font-semibold text-[#6B7280]'>
            Inactive
          </span>
        )
      default:
        return <span className='text-[13px] text-[#0A1628]'>{content[column.id]}</span>
    }
  }

  return (
    <>
      {/* Mobile card list */}
      <div className='min-[435px]:hidden mt-4 space-y-2 pb-24'>
        {productsDisplayed.length === 0 ? (
          <div className='rounded-xl border border-[#D1D5D8] bg-white px-4 py-10 text-center text-[14px] text-[#6B7280]'>
            No Products Found
          </div>
        ) : (
          productsDisplayed.map((content, index) => (
            <button
              key={content.id || index}
              type='button'
              onClick={() => onSelectProduct(content, 'name')}
              className='flex w-full items-center justify-between gap-3 rounded-xl border border-[#D1D5D8] bg-white px-4 py-3 text-left transition-colors hover:bg-[#F8FAFC]'
            >
              <div className='min-w-0 flex-1'>
                <div className='truncate text-[15px] font-semibold text-[#1F6FEB]'>{content.name}</div>
                <div className='mt-0.5 flex items-center gap-1.5 text-[12px] text-[#6B7280]'>
                  <span>{renderCategory(content)}</span>
                  <span className='text-[#CBD5E1]'>·</span>
                  <span className={`font-semibold ${content.is_active ? 'text-[#16803F]' : 'text-[#6B7280]'}`}>
                    {content.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className='shrink-0 text-[15px] font-bold text-[#0A1628]'>{currencyFormatter(content.price)}</div>
            </button>
          ))
        )}
      </div>

      <div className='hidden min-[435px]:block bg-white rounded-lg border border-[#D1D5D8] overflow-hidden mt-5'>
        {products.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
                  {columns.map((column) => {
                    const isSortable = column.id === 'name'
                    const isSorted = isSortable
                    return (
                      <TableHead
                        key={column.id}
                        style={{ minWidth: column.minWidth }}
                        className={`text-white text-[12px] font-semibold uppercase tracking-wide h-11 px-4 ${
                          column.align === 'center' ? 'text-center' : 'text-left'
                        } ${isSortable ? 'cursor-pointer select-none' : ''}`}
                        onClick={() => clickSortHandler(column.id)}
                      >
                        <span className='inline-flex items-center gap-1'>
                          {column.label}
                          {isSorted && (sort === 'ASC' ? <ChevronUp className='h-3.5 w-3.5' /> : <ChevronDown className='h-3.5 w-3.5' />)}
                        </span>
                      </TableHead>
                    )
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {productsDisplayed.map((content, index) => (
                  <TableRow
                    key={content.id || index}
                    className='h-[56px] even:bg-[#F8FAFC] hover:bg-[#F0F4FF] border-b border-[#EBEDF0] cursor-pointer'
                    onClick={() => onSelectProduct(content, 'name')}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={`px-4 py-2 align-middle ${column.align === 'center' ? 'text-center' : 'text-left'}`}
                        onClick={(e) => {
                          if (column.id === 'name') {
                            e.stopPropagation()
                          }
                        }}
                      >
                        {renderCell(content, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className='flex items-center justify-between px-5 py-3 border-t border-[#EBEDF0]'>
              <div className='w-[20%]'>
                <ItemPerPageSelector
                  itemPerPageList={[25, 50, 100, 200]}
                  pageSizeHandler={pageSizeHandler}
                  pageSize={pageParams.pageSize}
                />
              </div>
              <AdasPagination onChange={paginationHandler} page={pageParams.pageNo + 1} count={pageParams.totalPages} />
            </div>
          </>
        ) : (
          <AdasBox display='flex' alignItems='center' justifyContent='center' minHeight='200px'>
            No Products Found
          </AdasBox>
        )}
      </div>
    </>
  )
}

ProductList.propTypes = {
  products: PropTypes.array,
  onSelectProduct: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  pageParams: PropTypes.shape({
    pageNo: PropTypes.number,
    pageSize: PropTypes.number,
    totalPages: PropTypes.number,
    totalCount: PropTypes.number
  }).isRequired,
  pageSizeHandler: PropTypes.func.isRequired,
  paginationHandler: PropTypes.func.isRequired
}
